import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../app/store';
import { useDeleteFeeScheduleMutation } from '../../../../../services/ContractService';

const DeleteFeeScheduleButton: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const feeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);
  const [deleteFeeSchedule, { isLoading }] = useDeleteFeeScheduleMutation();
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const company = searchParams.get('company') || '';

  const handleOpenDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
  };

  const handleDelete = async () => {
    if (!feeSchedule || !feeSchedule.id || !feeSchedule.contractId) {
      setErrorMessage('Fee schedule information is incomplete');
      setShowError(true);
      handleCloseDialog();
      return;
    }

    try {
      console.log('Deleting fee schedule:', feeSchedule.id);
      
      await deleteFeeSchedule({
        id: feeSchedule.id,
        contractId: feeSchedule.contractId
      }).unwrap();

      console.log('Fee schedule deleted successfully');
      
      // Close dialog
      handleCloseDialog();
      
      // Show success message
      setShowSuccess(true);
      
      // Navigate back to the previous page
      setTimeout(() => {
        navigate(-1);
      }, 1500);
      
    } catch (error: any) {
      console.error('Error deleting fee schedule:', error);
      
      // Handle different types of errors
      let message = 'Failed to delete fee schedule';
      if (error?.data?.message) {
        message = error.data.message;
      } else if (error?.message) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }
      
      setErrorMessage(message);
      setShowError(true);
      handleCloseDialog();
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage('');
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={handleOpenDialog}
        disabled={!feeSchedule || isLoading}
        sx={{ minWidth: 120 }}
      >
        Delete
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Fee Schedule?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this fee schedule?
            <br /><br />
            <strong>Fee Schedule:</strong> {feeSchedule?.description || 'Unnamed Fee Schedule'}
            <br />
            <strong>ID:</strong> {feeSchedule?.id}
            <br /><br />
            This action cannot be undone. The fee schedule will be marked as deleted and will no longer appear in lists or be accessible.
            {feeSchedule?.feeSummary && feeSchedule.feeSummary.total > 0 && (
              <>
                <br /><br />
                <strong>Note:</strong> This fee schedule contains {feeSchedule.feeSummary.total || 0} code(s).
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" autoFocus disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          Fee schedule deleted successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeleteFeeScheduleButton;
